import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Send, RotateCcw, Clock, PauseCircle, Gauge } from "lucide-react";
import { toast } from "sonner";

/**
 * RehomeDialog — a case every desk passed on, put back out to work.
 * The three best-fitting desks that haven't already seen it come pre-ticked; nothing leaves
 * until Send is pressed. Paused and over-cap desks are shown but never pre-ticked.
 */
export function RehomeDialog({ leadId, open, onOpenChange, matched = [], onDone }) {
  const options = useMemo(
    () => matched.filter((m) => !m.already_sent)
      .sort((a, b) => (b.fit?.score || 0) - (a.fit?.score || 0)),
    [matched],
  );
  const [picked, setPicked] = useState([]);
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    const best = options.filter((m) => !m.capacity?.paused && !m.capacity?.at_capacity).slice(0, 3);
    setPicked((best.length ? best : options.slice(0, 3)).map((m) => m.id));
    setNotes("");
  }, [open, options]);

  const toggle = (id) => setPicked((p) => p.includes(id) ? p.filter((x) => x !== id) : [...p, id]);

  const send = async () => {
    if (!picked.length) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/hub/leads/${leadId}/rehome`, {
        lender_ids: picked, notes: notes.trim() || null,
      });
      toast.success(`Back out to ${data.routes_added} desk${data.routes_added === 1 ? "" : "s"}`, {
        description: "The case is live again and the passed-on alert has been cleared.",
      });
      (data.warnings || []).forEach((w) => toast.warning(w, { duration: 7000 }));
      onOpenChange?.(false);
      onDone?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "That didn't send");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={!!open} onOpenChange={(o) => { if (!busy) onOpenChange?.(o); }}>
      <DialogContent className="sm:max-w-lg" data-testid="rehome-dialog">
        <DialogHeader>
          <DialogTitle className="text-[17px]">Send this case to a new desk</DialogTitle>
          <DialogDescription className="text-[13px]">
            The best-fitting desks that haven't seen it yet, already ticked. Untick any you'd rather
            hold back — nothing goes until you press Send.
          </DialogDescription>
        </DialogHeader>

        {options.length === 0 ? (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 text-[12.5px] text-slate-600"
               data-testid="rehome-empty">
            Every matching desk has already seen this one. Widen a desk's finance types or deal band
            on the Lenders tab, or add a new desk, and it'll show up here.
          </div>
        ) : (
          <div className="space-y-2.5">
            <div className="max-h-72 space-y-1.5 overflow-y-auto pr-1" data-testid="rehome-list">
              {options.map((m) => {
                const on = picked.includes(m.id);
                const paused = m.capacity?.paused;
                const full = m.capacity?.at_capacity;
                return (
                  <label key={m.id}
                         className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${
                           on ? "border-[#0055FF] bg-[#0055FF]/[0.04]" : "border-slate-200 bg-white hover:border-slate-300"}`}
                         data-testid={`rehome-option-${m.id}`}>
                    <input type="checkbox" checked={on} onChange={() => toggle(m.id)}
                           className="size-4 accent-[#0055FF]" data-testid={`rehome-check-${m.id}`} />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-[13px] font-semibold text-slate-900">{m.company_name}</div>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-slate-500">
                        <span>{m.commission_type === "flat"
                          ? `£${Math.round(m.commission_flat_gbp || 0).toLocaleString()} flat`
                          : `${m.commission_pct}%`}</span>
                        {m.turnaround_days != null && (
                          <span className="inline-flex items-center gap-1"><Clock className="size-2.5" />{m.turnaround_days}d</span>
                        )}
                        {paused && (
                          <span className="inline-flex items-center gap-1 text-[#3B63A8]"><PauseCircle className="size-2.5" />desk paused</span>
                        )}
                        {!paused && full && (
                          <span className="inline-flex items-center gap-1 text-[#3B63A8]"><Gauge className="size-2.5" />over their cap</span>
                        )}
                      </div>
                    </div>
                    {m.fit?.score != null && (
                      <span className="hub-num shrink-0 rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] text-slate-500">
                        {m.fit.score}% fit
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)}
                      placeholder="Anything the new desks should know — why the others passed, what's changed…"
                      className="min-h-[76px] resize-none" data-testid="rehome-notes" />
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange?.(false)} disabled={busy}
                  className="rounded-full" data-testid="rehome-cancel">
            {options.length ? "Cancel" : "Close"}
          </Button>
          {options.length > 0 && (
            <Button onClick={send} disabled={busy || !picked.length}
                    className="rounded-full bg-[#0055FF] hover:bg-[#0044CC]" data-testid="rehome-send">
              {busy ? <Loader2 className="size-4 animate-spin" />
                    : <><Send className="mr-1.5 size-3.5" /> Send to {picked.length} desk{picked.length === 1 ? "" : "s"}</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export const RehomeButton = ({ onClick, testId = "lead-rehome-open" }) => (
  <button onClick={onClick} data-testid={testId}
          className="inline-flex h-8 items-center gap-1.5 rounded-[10px] bg-white px-3.5 text-[12px] font-semibold text-[#0B1220] transition-colors hover:bg-slate-100">
    <RotateCcw className="size-3" /> Send it to a new desk
  </button>
);
